import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { FormContainer } from '@/components/FormContainer'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { DateFieldContainer } from '@/components/inputs/DateFieldContainer'
import { DEPENDENT_RELATIONSHIPS } from '@/features/jobs/details/tax-type/tax-prep-organizer/data/dependent-relationships'
import type { AddDependentDialogViewModel } from './AddDependentDialogViewModel'
import { FormFieldGroup } from '@/components/Form'
import { MandatoryFieldsSubtitle } from '../../../components/MandatoryFieldsSubtitle'
import { masks } from '@/utils/input-mask-util'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'

interface AddDependentDialogProps {
  vm: AddDependentDialogViewModel
  testId?: string
}

export const AddDependentDialog = observer(function AddDependentDialog({
  vm,
  testId,
}: AddDependentDialogProps) {
  const formId = useId()

  return (
    <ModalDialog
      isOpen={vm.modal.showing}
      onClose={vm.closeDialog}
      isDismissable
      isUsingDialogScrolling
      size="lg"
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>New dependent</ModalDialogTitle>
        <ModalDialogDescription>
          <MandatoryFieldsSubtitle />
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogContent>
        <FormContainer
          id={formId}
          onSubmit={vm.save}
          testId={testId ? `${testId}-form` : undefined}
        >
          <FormFieldGroup stackWhenCompact>
            <TextFieldContainer
              model={vm}
              field="firstName"
              label={<FormFieldRequiredLabel label="First name" />}
              placeholder="e.g. Emily"
              required
              testId={testId ? `${testId}-first-name-input` : undefined}
            />
            <TextFieldContainer
              model={vm}
              field="middleInitial"
              label="M.I."
              placeholder="e.g. R"
              testId={testId ? `${testId}-middle-initial-input` : undefined}
            />
            <TextFieldContainer
              model={vm}
              field="lastName"
              label={<FormFieldRequiredLabel label="Last name" />}
              placeholder="e.g. Doe"
              required
              testId={testId ? `${testId}-last-name-input` : undefined}
            />
          </FormFieldGroup>
          <FormFieldGroup stackWhenCompact>
            <TextFieldContainer
              model={vm}
              field="tin"
              label={<FormFieldRequiredLabel label="SSN" />}
              placeholder="e.g. 123-45-6789"
              mask={masks.ssnOrItin}
              required
              data-private
              testId={testId ? `${testId}-ssn-input` : undefined}
            />
            <DateFieldContainer
              model={vm}
              field="dateOfBirth"
              label={<FormFieldRequiredLabel label="Date of birth" />}
              placeholder="MM/DD/YYYY"
              data-private
              testId={testId ? `${testId}-date-of-birth-input` : undefined}
            />
            <SelectContainer
              model={vm}
              field="relationship"
              label={<FormFieldRequiredLabel label="Relationship" />}
              items={DEPENDENT_RELATIONSHIPS}
              getKey={([code]) => code}
              getLabel={([, label]) => label}
              required
              testId={testId ? `${testId}-relationship-select` : undefined}
            />
          </FormFieldGroup>
        </FormContainer>
      </ModalDialogContent>

      <ModalDialogFooter sticky bordered compact>
        <ButtonGroup stretch>
          <Button
            variant="outline"
            label="Cancel"
            onPress={vm.closeDialog}
            testId={testId ? `${testId}-cancel-button` : undefined}
          />
          <Button
            variant="default"
            label="Add dependent"
            type="submit"
            form={formId}
            testId={testId ? `${testId}-save-button` : undefined}
          />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
})
